import type { NavigateFunction } from 'react-router-dom'
import { ensureSpSessionHydrated } from '@/features/auth/api/useAuth'
import { ApiError } from '@/api/http/types'
import { lockoutFromApiError } from '@/features/auth/lib/lockout'
import { INSTRUMENT_INFO_PATH_PREFIX } from './constants'
import { INVALID_INSTRUMENT_LINK_MESSAGE, instrumentLinkLoginState } from './instrument-link-routing'
import type {
  ConsentDeclineKind,
  InstrumentOnboardingStep,
  SpInstrumentLinkStartData,
  SpOnboardingChannel,
} from './types'

export const INSTRUMENT_OTP_EXPIRED_MESSAGE =
  'Your OTP has expired. Please request a new one.'

export function resolveInstrumentLinkStartError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.status === 400 || error.status === 404 || error.status === 410
      ? INVALID_INSTRUMENT_LINK_MESSAGE
      : error.message
  }
  return 'Something went wrong. Please try again.'
}

export function resolveOtpSendError(error: unknown): {
  message: string
  lockoutUntilMs: number | null
  blocked: boolean
} {
  if (error instanceof ApiError) {
    const lockout = lockoutFromApiError(error)
    return {
      message: error.message || 'OTP could not be sent. Please try again.',
      lockoutUntilMs: lockout?.lockedUntilMs ?? null,
      blocked: error.status === 403,
    }
  }
  return { message: 'OTP could not be sent. Please try again.', lockoutUntilMs: null, blocked: false }
}

export function resolveOtpVerifyError(error: unknown): {
  serverMessage: string | null
  inlineMessage: string | null
  lockoutUntilMs: number | null
} {
  if (!(error instanceof ApiError)) {
    return {
      serverMessage: 'An unexpected error occurred. Please try again.',
      inlineMessage: null,
      lockoutUntilMs: null,
    }
  }
  const lockout = lockoutFromApiError(error)
  if (lockout) {
    return { serverMessage: error.message, inlineMessage: null, lockoutUntilMs: lockout.lockedUntilMs }
  }
  if (error.status === 410) {
    return {
      serverMessage: INSTRUMENT_OTP_EXPIRED_MESSAGE,
      inlineMessage: null,
      lockoutUntilMs: null,
    }
  }
  return {
    serverMessage: null,
    inlineMessage: error.message || 'Please enter a valid verification code',
    lockoutUntilMs: null,
  }
}

export function resolveConsentMutationError(error: unknown): string {
  return error instanceof ApiError
    ? error.message
    : 'Something went wrong. Please try again.'
}

export function resolveRetryConsentStep(
  declinedKind: ConsentDeclineKind | null,
  skipPrivacy = false,
): 'privacy' | 'terms' {
  if (skipPrivacy) return 'terms'
  return declinedKind === 'terms' ? 'terms' : 'privacy'
}

export function resolveOtpResendError(error: unknown): {
  message: string | null
  resendCooldownUntilMs: number | null
} {
  if (!(error instanceof ApiError)) {
    return { message: null, resendCooldownUntilMs: null }
  }
  const lockout = lockoutFromApiError(error)
  const resendCooldownUntilMs = lockout?.retryAfterSeconds
    ? Date.now() + lockout.retryAfterSeconds * 1000
    : null
  return { message: error.message, resendCooldownUntilMs }
}

export function channelFromLinkStart(data: SpInstrumentLinkStartData): SpOnboardingChannel {
  const availability = data.channelAvailability ?? { email: true, sms: false }
  if (availability.email) return 'email'
  if (availability.sms) return 'sms'
  return data.channels?.[0] ?? 'email'
}

interface InstrumentLinkStartActions {
  goToInstrumentInfo: (instrumentId: string) => void
  navigateToLogin: (instrumentId: string) => void
  /** Sync entry point — implementations may run async work internally. */
  finishAfterConsent: (instrumentId: string) => void
  setChannel: (channel: SpOnboardingChannel) => void
  setStep: (step: InstrumentOnboardingStep) => void
}

interface ResolveInstrumentConsentOptions {
  skipPrivacy?: boolean
  /** Sign-up share `/instruments/:id`: never open Instrument Info (legacy option). */
  skipInstrumentInfo?: boolean
  /** Sign-up share: always show Privacy then Terms, even if identity already recorded consent. */
  forceBothPolicies?: boolean
}

export function routeInstrumentLinkStartFlow(
  data: SpInstrumentLinkStartData,
  actions: InstrumentLinkStartActions,
  options?: ResolveInstrumentConsentOptions,
): void {
  switch (data.flow) {
    case 'instrument':
      if (options?.skipInstrumentInfo) {
        resolveInstrumentConsentStep(data.consent, data.instrumentId, actions, options)
        return
      }
      actions.goToInstrumentInfo(data.instrumentId)
      return
    case 'login':
      actions.navigateToLogin(data.instrumentId)
      return
    case 'consent':
      resolveInstrumentConsentStep(data.consent, data.instrumentId, actions, options)
      return
    case 'onboarding':
    default:
      actions.setChannel(channelFromLinkStart(data))
      actions.setStep('channel')
  }
}

export function resolveInstrumentConsentStep(
  requirements: { privacyRequired: boolean; termsRequired: boolean },
  instrumentId: string | undefined,
  actions: Pick<InstrumentLinkStartActions, 'finishAfterConsent' | 'setStep'> & {
    resetDeclined?: () => void
  },
  options?: ResolveInstrumentConsentOptions,
): void {
  if (!instrumentId) return
  if (options?.forceBothPolicies) {
    actions.setStep('privacy')
    actions.resetDeclined?.()
    return
  }
  const skipPrivacy = options?.skipPrivacy === true
  if (skipPrivacy) {
    actions.setStep('terms')
    actions.resetDeclined?.()
    return
  }
  if (requirements.privacyRequired) {
    actions.setStep('privacy')
    actions.resetDeclined?.()
    return
  }
  if (requirements.termsRequired) {
    actions.setStep('terms')
    actions.resetDeclined?.()
    return
  }
  actions.finishAfterConsent(instrumentId)
}

export async function ensureInstrumentLinkSessionOrLogin(
  instrumentId: string,
  navigate: NavigateFunction,
): Promise<boolean> {
  const authed = await ensureSpSessionHydrated()
  if (authed) return true
  navigate('/login', {
    replace: true,
    state: instrumentLinkLoginState(instrumentId),
  })
  return false
}

export function instrumentInfoPath(instrumentId: string): string {
  return `${INSTRUMENT_INFO_PATH_PREFIX}/${instrumentId}/info`
}

export function surveyExecutionPath(instrumentId: string): string {
  return `${INSTRUMENT_INFO_PATH_PREFIX}/${instrumentId}/survey`
}
